/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-console */
import React, { useEffect, useState, useRef, memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useIdleTimer } from "react-idle-timer";
import { includes } from "lodash";
import { differenceInMinutes, differenceInSeconds } from "date-fns";
import PropTypes from "prop-types";

import { USER_IDLE_TIMEOUT, EXPIRY_INTERVAL } from "../../constants";
import Alert from "./components/Alert";
import routes from "../../routes";

let reminderTimer = EXPIRY_INTERVAL;
export const SessionExpiration = memo(props => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isExpired, setExpiredStatus] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const isExpiredRef = useRef(isExpired);
  const showReminderRef = useRef(showReminder);
  const [expiryTime, setExpiryTime] = useState(reminderTimer);
  const [extendExpiryTime, setExtendExpiryTime] = useState(false);
  const [isAgent, setIsAgent] = useState(false);
  const { uploadDocuments, completedSteps, expired, authToken, isAuthenticated } = props;
  const skippedPages = [
    routes.userAuthorized,
    routes.additionalDocumentsSubmitted,
    routes.consentAuthorized
  ];
  const currentPage = location.pathname;

  useEffect(() => {
    reset();
    setExtendExpiryTime(true);
  }, [uploadDocuments, completedSteps, location.pathname]);

  useEffect(() => {
    if (expired === true) {
      logout();
    }
  }, [expired]);

  useEffect(() => {
    if (extendExpiryTime) {
      setExtendExpiryTime(false);
      _setShowReminder(false);
      setExpiryTime(EXPIRY_INTERVAL);
      clearInterval(intervalRef);
      intervalRef = null;
    }
    return () => {
      clearInterval(intervalRef);
      intervalRef = null;
    };
  }, [extendExpiryTime]);

  const _setShowReminder = value => {
    showReminderRef.current = value;
    setShowReminder(value);
  };

  const _setExpiredStatus = value => {
    isExpiredRef.current = value;
    setExpiredStatus(value);
  };

  let intervalRef = null;
  const startExpiryInterval = (remaining = 0) => {
    reminderTimer = remaining ? remaining : EXPIRY_INTERVAL;
    _setShowReminder(true);
    if (intervalRef === null) {
      // clearInterval();
      intervalRef = setInterval(() => {
        if (reminderTimer > 0) {
          reminderTimer--;
          setExpiryTime(reminderTimer);
        } else {
          clearInterval(intervalRef);
          intervalRef = null;
          if (showReminderRef.current) {
            logout();
          }
        }
      }, 1000);
    }
  };

  const handleOnIdle = event => {
    if (authToken && typeof authToken !== "undefined" && !includes(skippedPages, currentPage)) {
      startExpiryInterval();
    }
  };

  document.addEventListener("visibilitychange", function() {
    if (document.visibilityState === "visible") {
      const timeout = (USER_IDLE_TIMEOUT + EXPIRY_INTERVAL * 1000) / (60 * 1000);
      const inactive = differenceInMinutes(new Date(), new Date(getLastActiveTime()));
      if (
        !showReminderRef.current &&
        !isExpiredRef.current &&
        inactive >= timeout &&
        authToken &&
        typeof authToken !== "undefined" &&
        !includes(skippedPages, currentPage)
      ) {
        logout();
      } else {
        if (
          !showReminderRef.current &&
          !isExpiredRef.current &&
          getRemainingTime() <= 0 &&
          authToken &&
          typeof authToken !== "undefined" &&
          !includes(skippedPages, currentPage)
        ) {
          const remaingCounter =
            timeout * 60 - differenceInSeconds(new Date(), new Date(getLastActiveTime()));
          startExpiryInterval(remaingCounter);
        }
      }
    }
  });

  const { reset, getLastActiveTime, getRemainingTime } = useIdleTimer({
    timeout: USER_IDLE_TIMEOUT,
    startManually: true,
    onIdle: handleOnIdle,
    debounce: 500,
    stopOnIdle: true,
    events: ["keydown"]
  });

  const logout = () => {
    _setExpiredStatus(true);
    _setShowReminder(false);
    props.setAccessToken("");
    if (isAuthenticated && authToken) {
      setIsAgent(true);
      props.logout({ agentId: props.agentId });
    }
  };

  if (!isExpired && !showReminder) {
    return null;
  }

  if (showReminder) {
    return (
      <Alert
        type="PROGRESS"
        isOpen={true}
        handleConfirm={() => {
          _setShowReminder(false);
          clearInterval(intervalRef);
          intervalRef = null;
          reset();
          setExtendExpiryTime(true);
        }}
        details={
          "Oops, looks like your session is about to expire. Would you like to continue on this page?"
        }
        percentage={(expiryTime / EXPIRY_INTERVAL) * 100}
        progressLabel={String(expiryTime)}
        isAgent={isAgent}
      />
    );
  } else {
    return (
      <Alert
        type="SUCCESS"
        isOpen={true}
        handleConfirm={() => {
          _setExpiredStatus(false);
          _setShowReminder(false);
          clearInterval(intervalRef);
          intervalRef = null;
          navigate(routes.comeBackLogin);
        }}
        details="Sorry, your session has expired. But don't worry, you can retrieve your application from where you left."
        isAgent={isAgent}
      />
    );
  }
});

SessionExpiration.displayName = "SessionExpiration";

SessionExpiration.propTypes = {
  uploadDocuments: PropTypes.any,
  completedSteps: PropTypes.any,
  expired: PropTypes.bool,
  authToken: PropTypes.string,
  isAuthenticated: PropTypes.bool,
  agentId: PropTypes.string,
  setAccessToken: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired
};
